package presentation.screens.profile

import analytics.Analytics
import com.kinoshazam.Navigator
import com.kinoshazam.analytics.AppEvents
import com.kinoshazam.data.GlobalStateRepository
import crashreporting.CrashReporter
import com.kinoshazam.data.PendingAuthCredentials
import com.kinoshazam.data.TokenRepository
import com.kinoshazam.model.AppLanguage
import com.kinoshazam.model.Route
import com.kinoshazam.model.ThemeState
import data.LoginRepository
import data.ProfileRepository
import kotlinx.coroutines.flow.combine
import models.User
import org.koin.core.component.KoinComponent
import presentation.screens.profile.ProfileState.BottomSheetState
import utils.BaseViewModel
import utils.launch

class ProfileViewModel(
    private val navigator: Navigator,
    private val repository: ProfileRepository,
    private val loginRepository: LoginRepository,
    private val tokenRepository: TokenRepository,
    private val pendingAuthCredentials: PendingAuthCredentials,
    private val globalStateRepository: GlobalStateRepository,
    private val analytics: Analytics,
    private val crashReporter: CrashReporter,
) : BaseViewModel<ProfileState, ProfileMVIAction>(), KoinComponent {

    init {
        authInit()
        themeInit()
        featuresInit()
        languageInit()
        updateInit()
    }

    override fun initState(): ProfileState = ProfileState(
        themeState = globalStateRepository.themeState.value,
        features = globalStateRepository.features.value,
        appLanguage = globalStateRepository.appLanguage.value,
        isLoading = globalStateRepository.isAuthorized.value &&
            globalStateRepository.profile.value == null,
        updateAvailable = globalStateRepository.appUpdate.value.isNeedUpdate,
        updateVersionName = globalStateRepository.appUpdate.value.versionName,
        forceUpdate = globalStateRepository.appUpdate.value.forceUpdate,
    )

    override fun onAction(action: ProfileMVIAction) {
        when (action) {
            is ProfileMVIAction.LogoutClicked -> onLogout()
            is ProfileMVIAction.LanguageChanged -> onLangChanged(action.language)
            is ProfileMVIAction.MenuClosed -> updateState { copy(bottomSheetState = BottomSheetState.Closed) }
            is ProfileMVIAction.OpenLanguageMenu -> updateState { copy(bottomSheetState = BottomSheetState.LanguageMenu) }
            is ProfileMVIAction.OpenThemeMenu -> updateState { copy(bottomSheetState = BottomSheetState.ThemeMenu) }
            is ProfileMVIAction.CloseMenu -> updateState { copy(bottomSheetState = BottomSheetState.Closed) }
            is ProfileMVIAction.ThemeSelected -> onThemeSelected(action.theme)
            is ProfileMVIAction.BackPressed -> {
                if (!state.value.forceUpdate) navigator.back()
            }
            is ProfileMVIAction.LoginPressed -> navigator.navigateTo(Route.Dialog.Login)
            is ProfileMVIAction.UpdateClicked -> navigator.navigateTo(Route.Update)
            is ProfileMVIAction.AppInfoClicked ->
                navigator.dismissModalAndNavigate(Route.AppInfo)
            is ProfileMVIAction.OpenHistory -> {
                if (state.value.user != null && state.value.features.watchVideo) {
                    navigator.dismissModalAndNavigate(Route.History)
                }
            }
        }
    }

    private fun onThemeSelected(theme: ThemeState) {
        repository.changeTheme(theme.name)
        globalStateRepository.setTheme(theme)
        updateState { copy(bottomSheetState = BottomSheetState.Closed) }
    }

    private fun onLangChanged(language: AppLanguage) {
        repository.changeLang(language.locale)
        globalStateRepository.setAppLanguage(language)
        updateState { copy(bottomSheetState = BottomSheetState.Closed) }
    }

    private fun authInit() {
        launch {
            combine(
                globalStateRepository.isAuthorized,
                globalStateRepository.profile,
            ) { authorized, profile ->
                authorized to profile
            }.collect { (authorized, profile) ->
                updateState {
                    copy(
                        isLoading = authorized && profile == null,
                        user = profile?.let {
                            User(
                                userName = it.userName,
                                email = it.email,
                                avatar = it.avatar,
                            )
                        },
                    )
                }
            }
        }
    }

    private fun themeInit() {
        launch {
            globalStateRepository.themeState.collect {
                updateState { copy(themeState = it) }
            }
        }
    }

    private fun featuresInit() {
        launch {
            globalStateRepository.features.collect {
                updateState { copy(features = it) }
            }
        }
    }

    private fun languageInit() {
        launch {
            globalStateRepository.appLanguage.collect {
                updateState { copy(appLanguage = it) }
            }
        }
    }

    private fun updateInit() {
        launch {
            globalStateRepository.appUpdate.collect { update ->
                updateState {
                    copy(
                        updateAvailable = update.isNeedUpdate,
                        updateVersionName = update.versionName,
                        forceUpdate = update.forceUpdate && update.isNeedUpdate,
                    )
                }
            }
        }
    }

    private fun onLogout() {
        launch {
            try {
                val refresh = tokenRepository.getRefreshToken()
                if (refresh != null) {
                    loginRepository.logout(refresh)
                }
            } catch (_: Throwable) {
                // Server may reject — local session must still be cleared
            } finally {
                pendingAuthCredentials.clear()
                tokenRepository.clear()
                analytics.track(AppEvents.logout())
                crashReporter.clearUser()
            }
        }
    }
}
